/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import TeletLogo from 'src/assets/logo/tellet-logo-main.svg';
import * as Yup from 'yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconLoader2 } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Image from '@/components/atoms/Image';
import Routes from '@/router/routes';
import axiosInstanceNoAuth from '@/axios/axiosInstanceNoAuth';
import ToastMessage from '@/components/toast-message/ToastMessage';
import { API_ENDPOINTS } from '@/api-endpoints';

// Mirrors backend phoneRegex in backend/libs/common/src/types/user.types.ts:20.
const phoneRegex = /^\+[1-9]\d{6,20}$/;

// TELV1-278 F1: form shape includes optional heal fields that are only rendered
// (and only required) when the preflight tells us the user is in half-state.
const defaultValues = {
  password: '',
  confirmPassword: '',
  name: '',
  last_name: '',
  phone_number: '',
};

type TPasswordResetForm = typeof defaultValues;

const buildSchema = (needsHeal: boolean) =>
  Yup.object().shape({
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
      .matches(/[0-9]/, 'Password must contain at least one number')
      .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character')
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('This field is required'),
    name: needsHeal
      ? Yup.string().trim().required('First name is required')
      : Yup.string().notRequired(),
    last_name: needsHeal
      ? Yup.string().trim().required('Last name is required')
      : Yup.string().notRequired(),
    phone_number: Yup.string()
      .notRequired()
      .test('phone-format', 'Phone must be in international format e.g. +31201234567', (value) => {
        if (!value || value.trim() === '') return true;
        return phoneRegex.test(value.trim());
      }),
  });

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenAccept = searchParams.get('token');

  // TELV1-278 F1: preflight gates whether to render heal fields.
  // While loading: render a minimal placeholder rather than the wrong form.
  // On failure: assume profile complete (worst case: server-side B5 will
  // 400 with PROFILE_INCOMPLETE if heal fields are actually required and
  // missing, so the user retries with a clear error).
  const [preflightLoading, setPreflightLoading] = useState(true);
  const [needsHeal, setNeedsHeal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues,
    resolver: yupResolver(buildSchema(needsHeal)),
  });

  useEffect(() => {
    let cancelled = false;
    const runPreflight = async () => {
      if (!tokenAccept) {
        // No token in URL at all — let the user attempt submit; backend will
        // 401 and we'll surface the standard error. This shouldn't happen
        // in normal flow (reset link always has ?token=...).
        setPreflightLoading(false);
        return;
      }
      try {
        const res = await axiosInstanceNoAuth.get(API_ENDPOINTS.USERS.RESET_PASSWORD_PREFLIGHT, {
          headers: { Authorization: `Bearer ${tokenAccept}` },
        });
        if (cancelled) return;
        const profileComplete = !!res.data?.profile_complete;
        setNeedsHeal(!profileComplete);
      } catch {
        // Network / token error. Fall through with needsHeal=false. If the
        // user IS half-state, backend B5 will return 400 PROFILE_INCOMPLETE
        // on submit and we'll surface a clear retry message.
      } finally {
        if (!cancelled) setPreflightLoading(false);
      }
    };
    runPreflight();
    return () => {
      cancelled = true;
    };
  }, [tokenAccept]);

  const onSubmit: SubmitHandler<TPasswordResetForm> = async (formData) => {
    try {
      // Strip the client-only confirmPassword field. Always send password.
      // Send heal fields ONLY when in heal mode (avoids accidentally writing
      // empty strings over a profile that already has values — though the
      // backend's race-safe conditional filter would catch it too).
      const payload: Record<string, string> = { password: formData.password };
      if (needsHeal) {
        payload.name = formData.name.trim();
        payload.last_name = formData.last_name.trim();
        if (formData.phone_number && formData.phone_number.trim()) {
          payload.phone_number = formData.phone_number.trim();
        }
      }

      await axiosInstanceNoAuth.post(API_ENDPOINTS.USERS.RESET_PASSWORD, payload, {
        headers: { Authorization: `Bearer ${tokenAccept}` },
      });
      setIsLoading(true);

      toast.success(
        <ToastMessage
          title="Password changed successfully"
          description={
            needsHeal
              ? 'Your account is now set up. Try to login.'
              : 'Password changed successfully try to login now.'
          }
          isAuthScreen
        />,
      );

      navigate(Routes.login.path);
    } catch {
      toast.error('Error occurred while changing password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (preflightLoading) {
    // Avoid flashing the wrong form while we discover whether to show heal
    // fields. Brief render (a fetch of ~50-200ms).
    return (
      <main className="flex items-center justify-center w-full h-full min-h-screen text-center bg-light-pink">
        <div className="flex items-center gap-2 text-[#2a3547]">
          <IconLoader2 className="w-5 h-5 animate-spin" />
          <span>Loading…</span>
        </div>
      </main>
    );
  }

  return (
    <main className="flex items-center justify-center w-full h-full min-h-screen text-center bg-light-pink">
      <div className="w-full max-w-[600px] min-h-screen center border-0 px-2 py-4 min450:bg-white min450:max-w-full">
        <div className="flex flex-col items-center w-full max-w-[600px] rounded-md h-fit p-7 min450:p-5">
          <div className="flex flex-col items-center w-full ring-1 ring-neutral-200 max-w-[500px] min450:ring-transparent min450:shadow-none gap-10 bg-white rounded-md shadow-md h-fit p-7">
            <Image effect="blur" src={TeletLogo} alt="logo" width={115} height={37} />
            {needsHeal && (
              <p className="text-sm text-[#2a3547] text-center">
                Welcome to Tellet — we noticed your account setup is not complete. Please fill in
                your details below to finish setting up your account.
              </p>
            )}
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col items-start w-full gap-5 h-fit"
            >
              {needsHeal && (
                <>
                  <label className="flex text-[#2a3547] flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative">
                    First name
                    <Input {...register('name')} type="text" hasError={!!errors.name} />
                    {errors.name?.message && (
                      <p className="absolute top-0 ml-1 text-sm text-red-600 right-2">
                        {errors.name?.message}
                      </p>
                    )}
                  </label>
                  <label className="flex text-[#2a3547] flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative">
                    Last name
                    <Input {...register('last_name')} type="text" hasError={!!errors.last_name} />
                    {errors.last_name?.message && (
                      <p className="absolute top-0 ml-1 text-sm text-red-600 right-2">
                        {errors.last_name?.message}
                      </p>
                    )}
                  </label>
                  <label className="flex text-[#2a3547] flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative">
                    Phone number (optional, international format e.g. +31201234567)
                    <Input
                      {...register('phone_number')}
                      type="tel"
                      hasError={!!errors.phone_number}
                    />
                    {errors.phone_number?.message && (
                      <p className="absolute top-0 ml-1 text-sm text-red-600 right-2">
                        {errors.phone_number?.message}
                      </p>
                    )}
                  </label>
                </>
              )}
              <label className="flex text-[#2a3547] flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative">
                Password
                <Input {...register('password')} type="password" hasError={!!errors.password} />
                {errors.password?.message && (
                  <p className="absolute top-0 ml-1 text-sm text-red-600 right-2">
                    {errors.password?.message}
                  </p>
                )}
              </label>
              <label className="flex text-[#2a3547] flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative">
                Confirm password
                <Input
                  {...register('confirmPassword')}
                  type="password"
                  hasError={!!errors.confirmPassword}
                />
                {errors.confirmPassword?.message && (
                  <p className="absolute top-0 ml-1 text-sm text-red-600 right-2">
                    {errors.confirmPassword?.message}
                  </p>
                )}
              </label>
              <div className="relative flex flex-col w-full gap-7">
                <Button
                  disabled={isSubmitting || isLoading}
                  variant="default"
                  type="submit"
                  className="flex items-center gap-0.5 mt-4"
                >
                  {isSubmitting && <IconLoader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <span>{needsHeal ? 'Complete account setup' : 'Reset password'}</span>
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}

export default ResetPassword;
